import type { AiCostLimitLevel } from '@Domain/AiCostLimits/AiCostLimitLevel';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type * as React from 'react';
import { Dropdown, InputText } from '@verclam/ui-kit';

const helpTextStyle = css`
	margin: 0.75rem 1.25rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

const SUBJECT_LABELS: Readonly<Record<Exclude<AiCostLimitLevel, 'account'>, string>> = {
	organization: 'Structure',
	facility: 'Agence',
	user: 'Utilisateur',
};

export type SubjectOption = {
	readonly id: number;
	readonly label: string;
};

type Props = {
	readonly level: Exclude<AiCostLimitLevel, 'account'>;
	readonly isReadOnly: boolean;
	readonly options: readonly SubjectOption[];
	readonly value: number | null;
	readonly subjectName: string;
	readonly errorMessage: string | undefined;
	readonly isValid: boolean;
	readonly onChange: (value: number | null) => void;
};

/** The perimeter of a limit: read-only when edited or fixed by the caller, a choice among the free subjects otherwise. */
const SubjectField: React.FC<Props> = ({
	level,
	isReadOnly,
	options,
	value,
	subjectName,
	errorMessage,
	isValid,
	onChange,
}) => {
	if (isReadOnly) {
		return (
			<InputText
				name="subject"
				label={SUBJECT_LABELS[level]}
				readOnly
				value={subjectName}
				errorMessage={errorMessage}
				isFormFieldValid={isValid}
			/>
		);
	}

	return (
		<ConditionalLoader
			data={options.length > 0}
			fallback={<p css={helpTextStyle}>Tous les périmètres de ce niveau ont déjà un plafond.</p>}
		>
			<Dropdown
				name="subjectId"
				label={SUBJECT_LABELS[level]}
				required
				filter={level === 'user'}
				options={options}
				optionLabel="label"
				optionValue="id"
				value={value}
				onChange={onChange}
				errorMessage={errorMessage}
				isFormFieldValid={isValid}
			/>
		</ConditionalLoader>
	);
};

export default SubjectField;
